import React, { useCallback, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { FiltersButton, InfoBanner, Pagination, Spinner, TableHeader } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CategoryTable from "../../ui-components/CategoryTable/CategoryTable";
import { CreateEditModalComponent } from "@me-pos/service-modal";
import i18next from "i18next";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import {
    GetProductCategoriesQuery,
    useCreateProductCategoryInTableMutation,
    useDeleteProductCategoryInTableMutation,
    useGetProductCategoriesQuery,
    useRestoreProductCategoryInTableMutation,
    useUpdateProductCategoryInTableMutation,
} from "@me-team/host/main/productCategories/graphql/productCategories.hooks";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import CategoryTableInnerService from "../../../services/CategoryTableServices/CategoryTableInnerService";
import { CategoryVariables } from "../../../interfaces/interfaces";
import { SearchComponent } from "../../Services/SearchComponent/SearchComponent";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import RequestInputService from "../services/RequestInputService";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { refreshProductCategoriesCache } from "../services/refreshProductCategoriesCache";

type ProductCategoryListItem = NonNullable<
    NonNullable<GetProductCategoriesQuery["productCategories"]>["items"]
>[number];

export const ProductCategories: React.FC = () => {
    const { t: translate } = useTranslation();
    const { currentCompanyId, branchId } = useParams();
    const client = useApolloClient();
    const canCreateProductCategory = useRoleAccess(CompanyAccess.ProductCategoryCreate);
    const canEditProductCategory = useRoleAccess(CompanyAccess.ProductCategoryEdit);
    const canDeleteRestoreProductCategory = useRoleAccess(CompanyAccess.ProductCategoryDeleteRestore);
    const canOpenProducts = useRoleAccess(CompanyAccess.ProductListView);
    const hasCategoryActions = canCreateProductCategory || canEditProductCategory || canDeleteRestoreProductCategory;
    const [searchValue, setSearchValue] = useState<string>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState<boolean>(false);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const [categoryListPerPage, setCategoryListPerPage] = useState<ProductCategoryListItem[]>([]);
    const [categoryList, setCategoryList] = useState<ProductCategoryListItem[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(1);
    const [pageCount, setPageCount] = useState(0);

    useScrollToTopOnChange(currentPage);

    const { refetch: refetchCategoryListPerPage, loading: loadingProductPerPage } = useGetProductCategoriesQuery({
        variables: {
            id: null,
            itemsPerPage: itemsPerPage,
            serviceDeleted: false,
            categoryName: searchValue ? searchValue.toLowerCase() : null,
            page: currentPage,
            branchId: Number(branchId),
        },
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            setCategoryListPerPage(data.productCategories?.items ?? []);
            setPageCount(parseInt(data.productCategories?.pageCount || "0"));
            setTotalItems(data.productCategories?.totalCount || 0);
        },
    });

    const { refetch: refetchCategoryList, loading: loadingProductList } = useGetProductCategoriesQuery({
        variables: {
            id: null,
            itemsPerPage: 1000,
            serviceDeleted: false,
            categoryName: null,
            page: 1,
            branchId: Number(branchId),
        },
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            setCategoryList(data.productCategories?.items ?? []);
        },
    });

    const refetchData = useCallback(
        async (testToShow: string) => {
            try {
                await refreshProductCategoriesCache(client, branchId);

                const categoryListResponse = await refetchCategoryList();
                setCategoryList(categoryListResponse.data.productCategories?.items ?? []);

                const categoryListPerPageResponse = await refetchCategoryListPerPage();
                setCategoryListPerPage(categoryListPerPageResponse.data.productCategories?.items ?? []);
                setPageCount(parseInt(categoryListPerPageResponse.data.productCategories?.pageCount || "0"));
                setTotalItems(categoryListPerPageResponse.data.productCategories?.totalCount || 0);
                toggleShowToast(testToShow);
            } catch (error) {
                console.error(error);
            }
        },
        [branchId, client, refetchCategoryList, refetchCategoryListPerPage, toggleShowToast]
    );

    const [createCategory] = useCreateProductCategoryInTableMutation();
    const [deleteCategory] = useDeleteProductCategoryInTableMutation();
    const [restoreCategory] = useRestoreProductCategoryInTableMutation();
    const [updateCategory] = useUpdateProductCategoryInTableMutation();

    const handlePageChange = useCallback((pageNumber: number) => {
        setCurrentPage(pageNumber);
    }, []);

    const HandleToggleFilters = useCallback(() => setShowFilters((prev) => !prev), []);

    const searchHandling = useCallback((currentValue: string) => {
        setSearchValue(currentValue || null);
    }, []);

    const openCreateCategoryModalHandler = useCallback(() => {
        if (!canCreateProductCategory) return;
        setIsCreateCategoryModalOpen(true);
    }, [canCreateProductCategory]);

    const onCreateHandler = useCallback(
        async (categoryVariables: CategoryVariables) => {
            if (!canCreateProductCategory) return;

            await createCategory({
                variables: {
                    companyId: parseInt(currentCompanyId),
                    input: categoryVariables.categoryInput,
                    branchId: Number(branchId),
                },
                onCompleted: () => {
                    refetchData(translate("Created"));
                },
            });
        },
        [branchId, canCreateProductCategory, createCategory, currentCompanyId, refetchData, translate]
    );

    const onUpdateHandler = useCallback(
        async (categoryVariables: CategoryVariables, isRestored?: boolean) => {
            if (isRestored ? !canDeleteRestoreProductCategory : !canEditProductCategory) return;

            const mutateCategory = isRestored ? restoreCategory : updateCategory;

            await mutateCategory({
                variables: {
                    id: categoryVariables.categoryId,
                    input: categoryVariables.categoryInput,
                    branchId: Number(branchId),
                },
                onCompleted: () => {
                    refetchData(translate(isRestored ? "Restored" : "Saved"));
                },
            });
        },
        [
            branchId,
            canDeleteRestoreProductCategory,
            canEditProductCategory,
            restoreCategory,
            updateCategory,
            refetchData,
            translate,
        ]
    );

    const onDeleteHandler = useCallback(
        async (categoryVariables: CategoryVariables) => {
            if (!canDeleteRestoreProductCategory) return;

            await deleteCategory({
                variables: {
                    productCategoryId: categoryVariables.categoryId,
                    branchId: Number(branchId),
                },
                onCompleted: () => {
                    refetchData(translate("Deleted"));
                },
            });
        },
        [branchId, canDeleteRestoreProductCategory, deleteCategory, refetchData, translate]
    );

    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    if (loadingProductList || loadingProductPerPage) return <Spinner />;

    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <p className="fs-7 mb-0">{translate("Create and edit product categories and subcategories.")}</p>
            </InfoBanner>
            <TableHeader
                title={translate("Product Categories")}
                paths={[
                    {
                        path: `/warehouse/product-categories/${currentCompanyId}/branch/${branchId}`,
                        label: translate("Warehouse accounting"),
                    },
                    { path: `/warehouse/product/${currentCompanyId}/branch/${branchId}`, label: translate("Products") },
                    {
                        path: `/warehouse/product-categories/${currentCompanyId}/branch/${branchId}`,
                        label: translate("Product Categories"),
                    },
                ]}
                buttons={
                    <Row className="gx-1 w-100 mt-4 mt-lg-0  d-flex flex-lg-nowrap justify-content-lg-end gap-3">
                        {canCreateProductCategory && (
                            <Col md={12} lg={5} xl={4}>
                                <Button
                                    variant="primary"
                                    className="w-100 text-white fw-bold text-truncate"
                                    onClick={openCreateCategoryModalHandler}
                                >
                                    <i className="bi bi-plus-lg fw-bold me-1 w-100 h-100"></i>
                                    {translate("Add Category")}
                                </Button>
                            </Col>
                        )}
                        <Col md={12} lg={3} xl={2} className="px-0 mb-3 mb-lg-0">
                            <FiltersButton
                                filter={{ searchValue }}
                                showFilters={showFilters}
                                onClick={HandleToggleFilters}
                                classesText={`${hasCategoryActions ? "d-none d-xxl-inline-block" : "d-inline-block"}`}
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <SearchComponent
                    value={searchValue}
                    placeholder={translate("Search by category/subcategory name")}
                    searchHandling={searchHandling}
                />
            </TableHeader>

            {categoryListPerPage && categoryListPerPage.length !== 0 && (
                <>
                    <CategoryTable
                        hasAccess={hasCategoryActions}
                        access={{
                            canCreateCategory: canCreateProductCategory,
                            canEditCategory: canEditProductCategory,
                            canDeleteRestoreCategory: canDeleteRestoreProductCategory,
                            canOpenItems: canOpenProducts,
                        }}
                        categoriesToShow={CategoryTableInnerService.ProductCategoryTableInner(categoryListPerPage)}
                        categoryList={CategoryTableInnerService.ProductCategoryTableInner(categoryList)}
                        onCreate={onCreateHandler}
                        onDelete={onDeleteHandler}
                        onUpdate={onUpdateHandler}
                        nameOfQtyColum={translate("Quantity of Products")}
                        searchParamBool={!!searchValue}
                        requestInputService={RequestInputService}
                        uniqueInfo={{
                            delErrorWithCategoryServices: translate(
                                "This category is associated with products. Please edit the products linked to this category before deletion."
                            ),
                            delModalBtnName: translate("Product Directory"),
                            refToService: `/warehouse/product/${currentCompanyId}/branch/${branchId}`,
                        }}
                    />
                    <div className="d-flex justify-content-between align-items-center my-4">
                        <Pagination
                            t={translate}
                            pages={Number(pageCount)}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            firstItemIndex={firstItemIndex}
                            lastItemIndex={lastItemIndex}
                            totalItems={totalItems}
                        />
                    </div>
                </>
            )}

            {categoryListPerPage && categoryListPerPage.length === 0 && (
                <p className={"mb-0 mt-5 pt-5 text-center fs-4"}>{translate("NoDataFoundForTheGivenValues")}</p>
            )}

            {canCreateProductCategory && isCreateCategoryModalOpen && (
                <CreateEditModalComponent
                    i18n={i18next}
                    categoryId={null}
                    subcategoryId={null}
                    isOpen={isCreateCategoryModalOpen}
                    categoryInput={RequestInputService.createServiceCategoryInput(null, null, [])}
                    categoryList={categoryList}
                    isCreateAction={isCreateCategoryModalOpen}
                    onClose={() => {
                        setIsCreateCategoryModalOpen(false);
                    }}
                    onSubmit={() => {}}
                    onSubmitCreate={async (inputVariables) => {
                        if (!canCreateProductCategory) return;

                        await createCategory({
                            variables: {
                                companyId: parseInt(currentCompanyId),
                                input: inputVariables,
                                branchId: Number(branchId),
                            },
                            onCompleted: () => {
                                setIsCreateCategoryModalOpen(false);
                                refetchData(translate("Created"));
                            },
                        });
                    }}
                    requestInputService={RequestInputService}
                    onSubmitUpdate={async () => {}}
                    currentCategory={null}
                    categoryService={null}
                />
            )}

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </div>
    );
};
